/**
 * The Ender dragon fight (vanilla EndDragonFight + EndCrystal + DragonFireball
 * + the breath cloud), DOM-free. One instance in main.ts; tick it at 20 Hz while
 * the player is in the End.
 *
 *   const fight = new DragonFight(host, { pillars: END_PILLARS, exit: END_EXIT_PORTAL });
 *   fight.loadFlags(save.flags);           // dragon killed + destroyed crystals
 *   fight.enter();                         // on arriving in the End: spawns dragon (unless killed)
 *   fight.tick();                          // every game tick
 *   fight.attackRay(...) / fight.projectileHit(...) / fight.onExplosion(...)
 *   fight.saveFlags()                      // string[] to merge into the world flags
 *   fight.bossBar()                        // purple "Ender Dragon" bar data (or null)
 *
 * Crystals stand on top of each pillar (`pillar.height` = the top block's y, so
 * the crystal's feet are at height + 1). Every damage source explodes one with
 * power 6; destroying the one that currently heals the dragon costs it 10 HP.
 */

import { BLOCKS, IS_OPAQUE } from '../../blocks';
import type { DamageSource } from '../../game/types';
import { segmentAabb } from '../host';
import {
  DEATH_TICKS, EnderDragon, type DragonEnv, type DragonPart, type EndLayout, type HurtKind,
} from './dragon';

export interface Crystal {
  id: number;
  pillar: number;
  /** Centre-bottom of the 2x2 hitbox. */
  x: number; y: number; z: number;
  alive: boolean;
  /** True while this is the crystal the dragon heals from (beam drawn). */
  beam: boolean;
  age: number;
}

export interface DragonFireball {
  id: number;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  age: number;
  removed: boolean;
}

/** Lingering dragon's breath: damages everyone inside 3 per second, shrinking as it fades. */
export interface BreathCloud {
  id: number;
  x: number; y: number; z: number;
  radius: number; startRadius: number;
  age: number; duration: number;
  cd: number;
}

export interface FightHost {
  getBlock(x: number, y: number, z: number): number;
  /** Remove a block without drops (the dragon smashing through). */
  breakBlock(x: number, y: number, z: number): void;
  player: {
    readonly x: number; readonly y: number; readonly z: number; readonly eyeY: number;
    readonly width: number; readonly height: number;
    readonly alive: boolean; readonly creative: boolean; readonly spectator: boolean;
    damage(amount: number, src: DamageSource): void | 'blocked';
    knockback(dx: number, dz: number, strength: number): void;
  };
  /** Block explosion + area damage (TntSim.blast / the entity host). */
  explode(x: number, y: number, z: number, power: number): void;
  dropXp(amount: number, x: number, y: number, z: number): void;
  /** Sound / particle cues. */
  effect?(kind: 'roar' | 'hurt' | 'flap' | 'growl' | 'death_blast' | 'crystal_blast' | 'fireball_burst', x: number, y: number, z: number): void;
  /**
   * The dragon died: turn the exit fountain on (worldgen/end.ts buildExitPortal(setBlock, true)).
   * `egg` is true on the first kill only; the glue must not leave an egg after repeat kills.
   */
  activatePortal(egg: boolean): void;
  /** The respawn ritual worked: the exit portal goes dark until the new dragon dies (the egg, if still there, stays). */
  deactivatePortal?(): void;
  /** Called once when the fight state changes in a way worth saving. */
  changed?(): void;
  rnd(): number;
}

const IMMUNE_NAMES = new Set([
  'Obsidian', 'Crying Obsidian', 'Bedrock', 'End Stone', 'Iron Bars', 'End Portal', 'End Portal Frame', 'Dragon Egg',
  'Barrier', 'Command Block', 'End Gateway',
]);

let immune: Uint8Array | null = null;
/** Blocks the dragon cannot destroy (vanilla dragon_immune: obsidian, end stone, bedrock, iron bars, ...). */
export function isDragonImmune(id: number): boolean {
  if (!immune || immune.length < BLOCKS.length) {
    immune = new Uint8Array(BLOCKS.length);
    for (let i = 0; i < BLOCKS.length; i++) if (BLOCKS[i] && IMMUNE_NAMES.has(BLOCKS[i].name)) immune[i] = 1;
  }
  return immune[id] === 1;
}

const KILLED_FLAG = 'dragon_killed';
const CRYSTAL_FLAG = 'dragon_crystal_dead:';
const HP_FLAG = 'dragon_hp:';
/** A dragon summoned back by the crystal ritual is still alive (`dragon_killed` alone would mean "no dragon"). */
const RESPAWNED_FLAG = 'dragon_respawned';

export class DragonFight {
  readonly crystals: Crystal[] = [];
  readonly fireballs: DragonFireball[] = [];
  readonly clouds: BreathCloud[] = [];
  dragon: EnderDragon | null = null;
  /** The dragon has been killed at least once in this world. */
  killed = false;
  /** A ritual-summoned dragon is alive (or mid-fight in the save). */
  private respawned = false;
  private nextId = 1;
  private readonly dead = new Set<number>();
  private readonly env: DragonEnv;
  private healing: Crystal | null = null;
  /** Dragon HP from the save, applied when it spawns. */
  private savedHealth: number | null = null;

  constructor(private readonly h: FightHost, readonly layout: EndLayout) {
    this.env = this.makeEnv();
  }

  // --- lifecycle -------------------------------------------------------------

  /** Arriving in the End: place the crystals still standing and spawn the dragon unless it is dead. */
  enter(): void {
    const fighting = !this.killed || this.respawned;
    if (this.crystals.length === 0 && fighting) this.spawnCrystals();
    if (fighting && !this.dragon) {
      const n = this.layout.exit;
      const d = new EnderDragon(this.env, this.layout, n.x + 60, n.y + 34, n.z);
      d.repeatKill = this.killed;
      if (this.savedHealth !== null) { d.health = Math.min(d.maxHealth, this.savedHealth); this.savedHealth = null; }
      this.dragon = d;
    }
  }

  private spawnCrystals(): void {
    this.layout.pillars.forEach((p, i) => {
      if (this.dead.has(i) || this.crystals.some((c) => c.alive && c.pillar === i)) return;
      this.crystals.push({ id: this.nextId++, pillar: i, x: p.x + 0.5, y: p.height + 1, z: p.z + 0.5, alive: true, beam: false, age: i * 7 });
    });
  }

  /** Leaving the End: transient projectiles and clouds go away, the dragon waits (full state kept). */
  leave(): void {
    this.fireballs.length = 0;
    this.clouds.length = 0;
  }

  crystalsAlive(): number {
    let n = 0;
    for (const c of this.crystals) if (c.alive) n++;
    return n;
  }

  // --- persistence -------------------------------------------------------------

  /** `dragon_killed` and `dragon_crystal_dead:<pillar>` flags for the world save. */
  saveFlags(): string[] {
    const out: string[] = [];
    if (this.killed) out.push(KILLED_FLAG);
    if (this.respawned) out.push(RESPAWNED_FLAG);
    for (const i of this.dead) out.push(CRYSTAL_FLAG + i);
    const d = this.dragon;
    // a save taken during the death animation brings it back on its last point of health
    if (d && !d.removed && d.health < d.maxHealth) out.push(HP_FLAG + Math.max(1, Math.ceil(d.health)));
    return out;
  }

  loadFlags(flags: readonly string[] | undefined): void {
    this.dead.clear();
    this.killed = false;
    this.respawned = false;
    this.savedHealth = null;
    for (const f of flags ?? []) {
      if (f === RESPAWNED_FLAG) { this.respawned = true; continue; }
      if (f.startsWith(HP_FLAG)) { const n = Number(f.slice(HP_FLAG.length)); if (n > 0) this.savedHealth = n; }
      else if (f === KILLED_FLAG) this.killed = true;
      else if (f.startsWith(CRYSTAL_FLAG)) { const i = Number(f.slice(CRYSTAL_FLAG.length)); if (Number.isInteger(i)) this.dead.add(i); }
    }
    this.crystals.length = 0;
    this.dragon = null;
  }

  // --- boss bar ----------------------------------------------------------------

  /** Purple boss bar data while the dragon lives; null otherwise. */
  bossBar(): { name: string; fraction: number } | null {
    const d = this.dragon;
    if (!d || d.removed) return null;
    return { name: 'Ender Dragon', fraction: Math.max(0, Math.min(1, d.health / d.maxHealth)) };
  }

  // --- tick ---------------------------------------------------------------------

  tick(): void {
    for (const c of this.crystals) if (c.alive) c.age++;
    const d = this.dragon;
    if (d) {
      // the healing beam: only the nearest crystal within 32 blocks of the dragon
      const near = d.alive ? this.nearestCrystal(d.x, d.y, d.z, 32) : null;
      this.healing = near;
      for (const c of this.crystals) c.beam = c === near;
      d.tick();
      if (d.removed) this.dragon = null;
    } else {
      this.healing = null;
      for (const c of this.crystals) c.beam = false;
    }
    this.tickFireballs();
    this.tickClouds();
    let w = 0;
    for (const c of this.crystals) if (c.alive) this.crystals[w++] = c;
    this.crystals.length = w;
  }

  private nearestCrystal(x: number, y: number, z: number, range: number): Crystal | null {
    let best: Crystal | null = null, bd = range * range;
    for (const c of this.crystals) {
      if (!c.alive) continue;
      const dd = (c.x - x) ** 2 + (c.y - y) ** 2 + (c.z - z) ** 2;
      if (dd <= bd) { bd = dd; best = c; }
    }
    return best;
  }

  // --- crystals -------------------------------------------------------------------

  /** Any damage detonates a crystal (power 6). `byPlayer` makes the dragon retaliate. */
  damageCrystal(c: Crystal, byPlayer: boolean): void {
    if (!c.alive) return;
    c.alive = false;
    const wasHealing = c === this.healing || c.beam;
    if (c.pillar >= 0) this.dead.add(c.pillar);
    this.h.effect?.('crystal_blast', c.x, c.y + 1, c.z);
    this.h.explode(c.x, c.y + 1, c.z, 6);
    this.dragon?.onCrystalDestroyed(byPlayer, wasHealing);
    this.h.changed?.();
  }

  /**
   * An explosion elsewhere (TNT, a bed, a creeper, another crystal) pops crystals
   * within its reach and hurts the dragon: explosions are the one non-player
   * damage it takes (vanilla `always_hurts_ender_dragons`), on the nearest part
   * with the usual falloff over 2 x power.
   */
  onExplosion(x: number, y: number, z: number, power: number): void {
    for (const c of this.crystals.slice()) {
      if (!c.alive) continue;
      if ((c.x - x) ** 2 + (c.y + 1 - y) ** 2 + (c.z - z) ** 2 <= (power + 1) ** 2) this.damageCrystal(c, false);
    }
    const d = this.dragon;
    if (!d || !d.alive) return;
    const radius = power * 2;
    let best: DragonPart | null = null, bd = radius;
    for (const p of d.parts()) {
      // distance to the part's box, not its centre: the body is 5 wide
      const ox = Math.max(0, Math.abs(p.x - x) - p.w / 2), oy = Math.max(0, Math.abs(p.y - y) - p.h / 2), oz = Math.max(0, Math.abs(p.z - z) - p.w / 2);
      const dist = Math.hypot(ox, oy, oz);
      if (dist < bd || (dist === bd && p.id === 'head')) { bd = dist; best = p; }
    }
    if (!best) return;
    const impact = 1 - bd / radius;
    d.hurtPart(best.id, Math.floor((impact * impact + impact) / 2 * 7 * radius + 1), 'explosion');
  }

  // --- player attacks -----------------------------------------------------------------

  /**
   * Melee / ray attack from the player's eye along unit `d` within `reach`.
   * Returns what was hit; crystals explode, the dragon part takes `damage` (with the head / body multiplier).
   */
  attackRay(
    ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
    reach: number, damage: number, kind: HurtKind = 'player',
  ): { target: 'crystal' | 'dragon'; part?: DragonPart; dealt: number } | null {
    let bestT = Infinity;
    let crystal: Crystal | null = null;
    let part: DragonPart | null = null;
    for (const c of this.crystals) {
      if (!c.alive) continue;
      const t = segmentAabb(ox, oy, oz, dx * reach, dy * reach, dz * reach, [c.x - 1, c.y, c.z - 1, c.x + 1, c.y + 2, c.z + 1]);
      if (t >= 0 && t < bestT) { bestT = t; crystal = c; part = null; }
    }
    const d = this.dragon;
    if (d && d.alive) {
      for (const p of d.parts()) {
        const t = segmentAabb(ox, oy, oz, dx * reach, dy * reach, dz * reach,
          [p.x - p.w / 2, p.y - p.h / 2, p.z - p.w / 2, p.x + p.w / 2, p.y + p.h / 2, p.z + p.w / 2]);
        if (t >= 0 && t < bestT) { bestT = t; part = p; crystal = null; }
      }
    }
    if (crystal) { this.damageCrystal(crystal, kind === 'player' || kind === 'projectile'); return { target: 'crystal', dealt: 0 }; }
    if (part && d) return { target: 'dragon', part, dealt: d.hurtPart(part.id, damage, kind) };
    return null;
  }

  /**
   * Arrow / thrown projectile travelling x0 -> x1 this tick. 'deflected' when it
   * bounced off the perched dragon (the glue should reverse / drop the arrow).
   */
  projectileHit(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, damage: number): 'hit' | 'deflected' | 'crystal' | null {
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
    const r = this.attackRay(x0, y0, z0, dx, dy, dz, 1, damage, 'projectile');
    if (!r) return null;
    if (r.target === 'crystal') return 'crystal';
    return r.dealt > 0 ? 'hit' : 'deflected';
  }

  // --- fireballs & clouds ------------------------------------------------------------------

  private spawnFireball(x: number, y: number, z: number, dx: number, dy: number, dz: number): void {
    const s = 0.9;
    this.fireballs.push({ id: this.nextId++, x, y, z, px: x, py: y, pz: z, vx: dx * s, vy: dy * s, vz: dz * s, age: 0, removed: false });
  }

  /**
   * A glass bottle used on the dragon's breath: when the view ray (length `reach`) passes through a lingering cloud, the
   * cloud shrinks by 0.5 blocks (vanilla AreaEffectCloud radius loss per bottle; gone below that) and the caller gets
   * Dragon's Breath. True when a bottle was filled.
   */
  takeBreath(ex: number, ey: number, ez: number, dx: number, dy: number, dz: number, reach: number): boolean {
    for (let t = 0.5; t <= reach; t += 0.25) {
      const px = ex + dx * t, py = ey + dy * t, pz = ez + dz * t;
      for (const c of this.clouds) {
        if (c.age >= c.duration) continue;
        if (Math.hypot(px - c.x, pz - c.z) > c.radius || py < c.y - 0.5 || py > c.y + 1.5) continue;
        c.radius -= 0.5;
        if (c.radius <= 0.1) c.age = c.duration; // spent: removed by the next tick
        return true;
      }
    }
    return false;
  }

  private addCloud(x: number, y: number, z: number, radius: number, duration: number): void {
    // merge into an existing cloud right there so a stream of breath doesn't stack damage
    for (const c of this.clouds) {
      if (Math.hypot(c.x - x, c.z - z) < 1.5 && Math.abs(c.y - y) < 1.5) {
        c.age = Math.min(c.age, duration * 0.3);
        c.radius = Math.max(c.radius, radius); c.startRadius = Math.max(c.startRadius, radius);
        return;
      }
    }
    if (this.clouds.length > 40) this.clouds.shift();
    this.clouds.push({ id: this.nextId++, x, y, z, radius, startRadius: radius, age: 0, duration, cd: 0 });
  }

  private tickFireballs(): void {
    const p = this.h.player;
    for (const f of this.fireballs) {
      f.px = f.x; f.py = f.y; f.pz = f.z;
      if (++f.age > 300) { f.removed = true; continue; }
      for (let s = 0; s < 3 && !f.removed; s++) {
        f.x += f.vx / 3; f.y += f.vy / 3; f.z += f.vz / 3;
        const hit = p.alive && Math.abs(f.x - p.x) < p.width / 2 + 0.5 && Math.abs(f.z - p.z) < p.width / 2 + 0.5 &&
          f.y > p.y - 0.5 && f.y < p.y + p.height + 0.5;
        const solid = IS_OPAQUE[this.h.getBlock(Math.floor(f.x), Math.floor(f.y), Math.floor(f.z))] === 1;
        if (hit || solid) {
          f.removed = true;
          this.h.effect?.('fireball_burst', f.x, f.y, f.z);
          this.addCloud(f.x - f.vx / 3, f.y - f.vy / 3, f.z - f.vz / 3, 3, 600);
        }
      }
    }
    let w = 0;
    for (const f of this.fireballs) if (!f.removed) this.fireballs[w++] = f;
    this.fireballs.length = w;
  }

  private tickClouds(): void {
    const p = this.h.player;
    for (const c of this.clouds) {
      c.age++;
      c.radius = c.startRadius * (1 - c.age / c.duration);
      if (c.cd > 0) c.cd--;
      if (c.cd === 0 && p.alive && !p.creative && !p.spectator &&
        Math.hypot(p.x - c.x, p.z - c.z) < c.radius && p.y > c.y - 1.2 && p.y < c.y + 2) {
        c.cd = 20;
        p.damage(3, { kind: 'generic', x: c.x, y: c.y, z: c.z });
      }
    }
    let w = 0;
    for (const c of this.clouds) if (c.age < c.duration) this.clouds[w++] = c;
    this.clouds.length = w;
  }

  // --- dragon environment ------------------------------------------------------------------------

  private makeEnv(): DragonEnv {
    const h = this.h;
    return {
      rnd: () => h.rnd(),
      crystalsAlive: () => this.crystalsAlive(),
      nearestCrystal: (x, y, z, range) => this.nearestCrystal(x, y, z, range),
      player: () => {
        const p = h.player;
        return { x: p.x, y: p.y, z: p.z, eyeY: p.eyeY, alive: p.alive, targetable: p.alive && !p.creative && !p.spectator };
      },
      los: (x0, y0, z0, x1, y1, z1) => {
        const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
        const n = Math.ceil(Math.hypot(dx, dy, dz) / 0.8);
        for (let i = 1; i < n; i++) {
          const t = i / n;
          if (IS_OPAQUE[h.getBlock(Math.floor(x0 + dx * t), Math.floor(y0 + dy * t), Math.floor(z0 + dz * t))] === 1) return false;
        }
        return true;
      },
      shootFireball: (x, y, z, dx, dy, dz) => this.spawnFireball(x, y, z, dx, dy, dz),
      breathCloud: (x, y, z, r, dur) => this.addCloud(x, y, z, r, dur),
      hitPlayer: (amount, fx, fz, knock) => {
        const p = h.player;
        if (!p.alive || p.creative || p.spectator) return;
        let blocked = false;
        if (amount > 0) blocked = p.damage(amount, { kind: 'melee', x: fx, y: p.y, z: fz }) === 'blocked';
        if (!blocked) p.knockback(p.x - fx, p.z - fz, knock);
      },
      smash: (x0, y0, z0, x1, y1, z1) => {
        let n = 0;
        for (let y = Math.floor(y0); y <= Math.floor(y1); y++) {
          for (let z = Math.floor(z0); z <= Math.floor(z1); z++) {
            for (let x = Math.floor(x0); x <= Math.floor(x1); x++) {
              const id = h.getBlock(x, y, z);
              if (id === 0 || isDragonImmune(id)) continue;
              h.breakBlock(x, y, z);
              if (++n > 200) return;
            }
          }
        }
      },
      event: (kind, x, y, z) => h.effect?.(kind, x, y, z),
      dropXp: (amount, x, y, z) => { if (amount > 0) h.dropXp(amount, x, y, z); },
      onDeath: () => {
        const first = !this.killed;
        this.killed = true;
        this.respawned = false;
        h.activatePortal(first);
        h.changed?.();
      },
    };
  }

  /** The four ritual spots: on the bedrock rim of the exit fountain, N / E / S / W. */
  ritualSpots(): Array<[number, number, number]> {
    const e = this.layout.exit;
    return [[e.x + 3, e.y + 1, e.z], [e.x - 3, e.y + 1, e.z], [e.x, e.y + 1, e.z + 3], [e.x, e.y + 1, e.z - 3]];
  }

  /**
   * An end crystal item was used on the block (bx, by, bz): needs obsidian / bedrock
   * under it and room. Returns true when placed. Placing the fourth crystal on the
   * fountain rim (dragon dead) starts the respawn ritual.
   */
  placeCrystal(bx: number, by: number, bz: number): boolean {
    const below = this.h.getBlock(bx, by, bz), above = this.h.getBlock(bx, by + 1, bz), above2 = this.h.getBlock(bx, by + 2, bz);
    if (!['Obsidian', 'Bedrock'].includes(BLOCKS[below]?.name ?? '')) return false;
    if (above !== 0 || above2 !== 0) return false;
    const x = bx + 0.5, y = by + 1, z = bz + 0.5;
    if (this.crystals.some((c) => c.alive && Math.abs(c.x - x) < 2 && Math.abs(c.z - z) < 2 && Math.abs(c.y - y) < 2)) return false;
    this.crystals.push({ id: this.nextId++, pillar: -1, x, y, z, alive: true, beam: false, age: 0 });
    this.tryRitual();
    return true;
  }

  /** Four crystals on the rim, dragon dead: they vanish, the pillar crystals return and the dragon respawns. */
  tryRitual(): boolean {
    if (this.dragon || !this.killed) return false;
    const spots = this.ritualSpots();
    const found: Crystal[] = [];
    for (const [sx, sy, sz] of spots) {
      const c = this.crystals.find((k) => k.alive && k.pillar < 0 && Math.abs(k.x - (sx + 0.5)) < 0.1 && Math.abs(k.z - (sz + 0.5)) < 0.1 && Math.abs(k.y - sy) < 0.1);
      if (!c) return false;
      found.push(c);
    }
    for (const c of found) c.alive = false;
    this.dead.clear();
    this.spawnCrystals();
    this.respawnDragon();
    this.respawned = true;
    this.h.deactivatePortal?.();
    this.h.changed?.();
    return true;
  }

  /** Spawn (or respawn) the dragon explicitly, e.g. after the summoning ritual. Repeat kills pay 500 XP. */
  respawnDragon(): void {
    if (this.dragon) return;
    const n = this.layout.exit;
    const d = new EnderDragon(this.env, this.layout, n.x + 60, n.y + 34, n.z);
    d.repeatKill = this.killed;
    this.dragon = d;
    if (!this.crystals.some((c) => c.alive && c.pillar >= 0)) { this.dead.clear(); this.spawnCrystals(); }
  }
}

export { DEATH_TICKS };
